import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Hook to restore scroll position when navigating back.
 * @param {boolean} isDataLoaded - Should be true when the data for the page is fully loaded and rendered.
 * @param {string} scrollKey - A unique key for storing the scroll position (e.g., pathname).
 */
const useScrollRestoration = (isDataLoaded, scrollKey) => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const hasRestored = useRef(false);

  // Reset the ref when pathname changes (moving to a new page)
  useEffect(() => {
    hasRestored.current = false;
  }, [pathname]);

  // Save scroll position before unmounting or navigating away
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        sessionStorage.setItem(`scrollPos_${scrollKey || pathname}`, window.scrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pathname, scrollKey]);

  // Restore scroll position when data is loaded
  useEffect(() => {
    if (!isDataLoaded || hasRestored.current) return;

    if (navigationType === 'POP') {
      const lastClickedAdId = sessionStorage.getItem('lastClickedAdId');
      const savedPosition = sessionStorage.getItem(`scrollPos_${scrollKey || pathname}`);

      let rafId1, rafId2;
      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          let scrollDone = false;

          // Try to find the specific ad element first (Target-based)
          if (lastClickedAdId) {
            const element = document.getElementById(lastClickedAdId);
            if (element) {
              element.scrollIntoView({ behavior: 'instant', block: 'center' });
              scrollDone = true;
            }
          }

          // Fallback to pixel-based if target not found or not applicable
          if (!scrollDone && savedPosition) {
            window.scrollTo({
              top: parseInt(savedPosition, 10),
              behavior: 'instant',
            });
          }

          hasRestored.current = true;
          // Clear the lastClickedAdId once used to avoid confusing future navigations
          sessionStorage.removeItem('lastClickedAdId');
        });
      });

      return () => {
        cancelAnimationFrame(rafId1);
        cancelAnimationFrame(rafId2);
      };
    } else if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      window.scrollTo({ top: 0, behavior: 'instant' });
      hasRestored.current = true;
    }
  }, [isDataLoaded, pathname, scrollKey, navigationType]);
};

export default useScrollRestoration;
